import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  Alert,
  Box,
  Button,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FilterSelect,
  FormControlLabel,
  HairlineCard,
  Icons,
  MenuItem,
  RowAction,
  RowActions,
  Stack,
  StatusChip,
  Switch,
  Tab,
  Tabs,
  TagChip,
  TextField,
  Typography,
  useConfirm,
  useRowActionsColumn,
} from '@munifocus/ui';
import {
  canEditTimeEntry,
  nbDay,
  type TimeEntryStatus,
  type TimeEntryView,
  type TimeSuggestion,
} from '@munifocus/contracts-gov';
import { AppShell } from '../../components/AppShell';
import { PageIntro } from '../../components/PageIntro';
import { useOptionalScope } from '../../providers/ScopeProvider';
import { useFiles } from '../../lib/files-api';
import { useMutationToast } from '../../lib/mutation-toast';
import {
  useDeleteTime,
  useEditTime,
  useMyTime,
  useRecordTime,
  useReviewTime,
  useTeamTime,
  useTimeSuggestions,
} from '../../lib/operations-api';
import {
  formatMinutes,
  splitForInput,
  timeStatusKind,
  toMinutes,
} from '../../lib/operations-format';
import { formatDate } from '../../lib/relative-time';
import { LoadError, Loading, OpsScopeNotice, useOpsMunicipality } from './shared';

/**
 * Time on enforcement work: the officer's own, and the supervisor's approvals.
 *
 * "My time" proposes entries from finished visits and on-duty sessions; the
 * officer accepts, adjusts or ignores them, and records anything else by hand.
 * "Approvals" is the active town's queue, for those who approve there — never
 * their own time, which the API refuses.
 */
export function TimeTracking() {
  const { t } = useTranslation();
  const scope = useOptionalScope();
  const status = scope?.operations ?? null;
  const canReport = !!status?.municipalities.some((m) => m.canReportTime);
  const approvals = useOpsMunicipality((m) => m.canApproveTime);
  const [tab, setTab] = useState<'mine' | 'approvals'>(canReport ? 'mine' : 'approvals');
  const current = !canReport ? 'approvals' : tab;

  return (
    <AppShell title={t('gov.ops.time.title')}>
      <PageIntro>{t('gov.ops.time.intro')}</PageIntro>
      {!status && <Loading />}
      {status && (
        <>
          <Tabs value={current} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            {canReport && <Tab value="mine" label={t('gov.ops.time.tabs.mine')} />}
            <Tab value="approvals" label={t('gov.ops.time.tabs.approvals')} />
          </Tabs>
          {current === 'mine' ? (
            <MyTime />
          ) : approvals.municipality ? (
            <Approvals lgId={approvals.municipality.localGovernmentId} />
          ) : (
            <OpsScopeNotice reason={approvals.reason ?? 'notEnabled'} />
          )}
        </>
      )}
    </AppShell>
  );
}

// ── My time ─────────────────────────────────────────────────────────────────

interface Draft {
  id?: string;
  localGovernmentId: string;
  fileId: string;
  workDate: string;
  hours: string;
  minutes: string;
  description: string;
  billable: boolean;
  source?: 'visit' | 'duty';
  sourceId?: string;
}

function MyTime() {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const confirm = useConfirm();
  const actionsColumn = useRowActionsColumn();
  const status = useOptionalScope()?.operations ?? null;
  const towns = (status?.municipalities ?? []).filter((m) => m.canReportTime);
  const entries = useMyTime();
  const suggestions = useTimeSuggestions();
  const record = useRecordTime();
  const remove = useDeleteTime();
  const [draft, setDraft] = useState<Draft | null>(null);
  const townName = (id: string) => towns.find((m) => m.localGovernmentId === id)?.name ?? '';

  const accept = async (s: TimeSuggestion) => {
    try {
      await record.mutateAsync({
        localGovernmentId: s.localGovernmentId,
        fileId: s.fileId,
        workDate: s.workDate,
        minutes: s.minutes,
        source: s.source,
        sourceId: s.sourceId,
      });
      toast.success('gov.ops.time.recorded');
    } catch (err) {
      toast.error(err, 'gov.ops.time.saveFailed');
    }
  };

  const fromSuggestion = (s: TimeSuggestion): Draft => ({
    localGovernmentId: s.localGovernmentId,
    fileId: s.fileId ?? '',
    workDate: s.workDate,
    ...splitForInput(s.minutes),
    description: '',
    billable: true,
    source: s.source,
    sourceId: s.sourceId,
  });

  const fromEntry = (e: TimeEntryView): Draft => ({
    id: e.id,
    localGovernmentId: e.localGovernmentId,
    fileId: e.fileId ?? '',
    workDate: e.workDate,
    ...splitForInput(e.minutes),
    description: e.description,
    billable: e.billable,
  });

  const onDelete = async (e: TimeEntryView) => {
    const ok = await confirm({
      title: t('gov.ops.time.confirmDelete'),
      body: t('gov.ops.time.confirmDeleteBody', {
        duration: formatMinutes(e.minutes),
        date: formatDate(e.workDate, i18n.language),
      }),
      confirmLabel: t('common.actions.delete'),
      cancelLabel: t('common.actions.cancel'),
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(e.id);
      toast.success('gov.ops.time.deleted');
    } catch (err) {
      toast.error(err, 'gov.ops.time.saveFailed');
    }
  };

  return (
    <Stack spacing={2}>
      <HairlineCard sx={{ p: 2 }}>
        <Typography variant="subtitle2" component="h2">
          {t('gov.ops.time.suggestionsTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('gov.ops.time.suggestionsHint')}
        </Typography>
        {suggestions.isLoading && <Loading />}
        {suggestions.isError && (
          <LoadError onRetry={() => void suggestions.refetch()}>
            {t('gov.ops.time.loadError')}
          </LoadError>
        )}
        {suggestions.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('gov.ops.time.noSuggestions')}
          </Typography>
        )}
        <Stack spacing={1}>
          {(suggestions.data ?? []).map((s) => (
            <Stack
              key={s.sourceId}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ alignItems: { sm: 'center' } }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {s.source === 'visit'
                    ? t('gov.ops.time.fromVisit', { file: s.fileReference ?? '' })
                    : t('gov.ops.time.fromDuty')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(s.workDate, i18n.language)} · {formatMinutes(s.minutes)} ·{' '}
                  {townName(s.localGovernmentId)}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={record.isPending}
                  onClick={() => void accept(s)}
                >
                  {t('gov.ops.time.accept')}
                </Button>
                <Button size="small" variant="outlined" onClick={() => setDraft(fromSuggestion(s))}>
                  {t('gov.ops.time.adjust')}
                </Button>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </HairlineCard>

      {entries.isError ? (
        <LoadError onRetry={() => void entries.refetch()}>{t('gov.ops.time.loadError')}</LoadError>
      ) : (
        <DataTable
          label={t('gov.ops.time.entriesTitle')}
          rows={entries.data ?? []}
          getRowKey={(e) => e.id}
          isLoading={entries.isLoading}
          emptyLabel={t('gov.ops.time.noEntries')}
          pageSize={25}
          action={
            towns.length > 0 ? (
              <ActionButton
                label={t('gov.ops.time.add')}
                icon={Icons.Plus}
                onClick={() =>
                  setDraft({
                    localGovernmentId: towns[0]?.localGovernmentId ?? '',
                    fileId: '',
                    workDate: nbDay(new Date()),
                    hours: '',
                    minutes: '',
                    description: '',
                    billable: true,
                  })
                }
              />
            ) : null
          }
          columns={[
            {
              key: 'date',
              label: t('gov.ops.time.date'),
              primary: true,
              sortValue: (e) => e.workDate,
              cell: (e) => formatDate(e.workDate, i18n.language),
            },
            {
              key: 'what',
              label: t('gov.ops.time.what'),
              cell: (e) => (
                <Stack>
                  <Typography variant="body2">
                    {e.fileReference ?? t('gov.ops.time.general')}
                  </Typography>
                  {e.description && (
                    <Typography variant="caption" color="text.secondary">
                      {e.description}
                    </Typography>
                  )}
                </Stack>
              ),
            },
            {
              key: 'minutes',
              label: t('gov.ops.time.duration'),
              align: 'right',
              sortValue: (e) => e.minutes,
              cell: (e) => formatMinutes(e.minutes),
            },
            {
              key: 'status',
              label: t('gov.ops.time.status'),
              sortValue: (e) => e.status,
              cell: (e) => <EntryStatus entry={e} />,
            },
            {
              ...actionsColumn,
              cell: (e) =>
                canEditTimeEntry(e, 'officer') ? (
                  <RowActions onEdit={() => setDraft(fromEntry(e))}>
                    <RowAction
                      label={t('common.actions.delete')}
                      icon={Icons.Trash2}
                      tone="danger"
                      onClick={() => void onDelete(e)}
                    />
                  </RowActions>
                ) : null,
            },
          ]}
        />
      )}

      {draft && <TimeDialog draft={draft} towns={towns} onClose={() => setDraft(null)} />}
    </Stack>
  );
}

function EntryStatus({ entry }: { entry: TimeEntryView }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
      <StatusChip
        status={timeStatusKind(entry.status)}
        label={t(`gov.ops.time.statuses.${entry.status}`)}
      />
      {entry.locked && <TagChip label={t('gov.ops.time.invoiced')} />}
      {entry.status === 'rejected' && entry.reviewNote && (
        <Typography variant="caption" color="text.secondary">
          {entry.reviewNote}
        </Typography>
      )}
    </Stack>
  );
}

function TimeDialog({
  draft: initial,
  towns,
  onClose,
}: {
  draft: Draft;
  towns: Array<{ localGovernmentId: string; name: string }>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useMutationToast();
  const record = useRecordTime();
  const edit = useEditTime();
  const files = useFiles();
  const [d, setD] = useState<Draft>(initial);
  const [tried, setTried] = useState(false);
  const minutes = toMinutes(d.hours, d.minutes);
  const today = nbDay(new Date());
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(d.workDate) && d.workDate <= today;
  const fileOptions = useMemo(
    () =>
      (files.data?.items ?? []).filter(
        (f) => f.localGovernmentId === d.localGovernmentId && f.status !== 'closed',
      ),
    [files.data, d.localGovernmentId],
  );
  const pending = record.isPending || edit.isPending;

  const submit = async () => {
    setTried(true);
    if (minutes === null || !dateOk || !d.localGovernmentId) return;
    try {
      if (d.id) {
        await edit.mutateAsync({
          id: d.id,
          fileId: d.fileId || null,
          workDate: d.workDate,
          minutes,
          description: d.description,
          billable: d.billable,
        });
      } else {
        await record.mutateAsync({
          localGovernmentId: d.localGovernmentId,
          fileId: d.fileId || null,
          workDate: d.workDate,
          minutes,
          description: d.description,
          billable: d.billable,
          ...(d.source && d.sourceId ? { source: d.source, sourceId: d.sourceId } : {}),
        });
      }
      toast.success(d.id ? 'gov.ops.time.updated' : 'gov.ops.time.recorded');
      onClose();
    } catch (err) {
      toast.error(err, 'gov.ops.time.saveFailed');
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t(d.id ? 'gov.ops.time.editTitle' : 'gov.ops.time.addTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label={t('gov.ops.time.municipality')}
            value={d.localGovernmentId}
            disabled={!!d.id || !!d.source}
            onChange={(e) => setD({ ...d, localGovernmentId: e.target.value, fileId: '' })}
          >
            {towns.map((m) => (
              <MenuItem key={m.localGovernmentId} value={m.localGovernmentId}>
                {m.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('gov.ops.time.file')}
            value={d.fileId}
            disabled={d.source === 'visit'}
            onChange={(e) => setD({ ...d, fileId: e.target.value })}
            helperText={t('gov.ops.time.fileHint')}
          >
            <MenuItem value="">{t('gov.ops.time.general')}</MenuItem>
            {d.fileId && !fileOptions.some((f) => f.id === d.fileId) && (
              <MenuItem value={d.fileId}>{t('gov.ops.time.thisFile')}</MenuItem>
            )}
            {fileOptions.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.reference} · {f.address}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            label={t('gov.ops.time.date')}
            value={d.workDate}
            onChange={(e) => setD({ ...d, workDate: e.target.value })}
            error={tried && !dateOk}
            helperText={tried && !dateOk ? t('gov.ops.time.dateInvalid') : ' '}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              type="number"
              label={t('gov.ops.time.hours')}
              value={d.hours}
              onChange={(e) => setD({ ...d, hours: e.target.value })}
              error={tried && minutes === null}
              slotProps={{ htmlInput: { min: 0, max: 24 } }}
              sx={{ flex: 1 }}
            />
            <TextField
              type="number"
              label={t('gov.ops.time.minutes')}
              value={d.minutes}
              onChange={(e) => setD({ ...d, minutes: e.target.value })}
              error={tried && minutes === null}
              slotProps={{ htmlInput: { min: 0, max: 59 } }}
              sx={{ flex: 1 }}
            />
          </Stack>
          {tried && minutes === null && (
            <Typography variant="caption" color="error">
              {t('gov.ops.time.durationInvalid')}
            </Typography>
          )}
          <TextField
            label={t('gov.ops.time.description')}
            value={d.description}
            multiline
            minRows={2}
            onChange={(e) => setD({ ...d, description: e.target.value })}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={d.billable}
                onChange={(e) => setD({ ...d, billable: e.target.checked })}
              />
            }
            label={t('gov.ops.time.billable')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button variant="contained" disabled={pending} onClick={() => void submit()}>
          {t('common.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Approvals ───────────────────────────────────────────────────────────────

export function Approvals({ lgId }: { lgId: string }) {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const [status, setStatus] = useState<TimeEntryStatus | 'all'>('submitted');
  const team = useTeamTime(lgId, status);
  const review = useReviewTime(lgId);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');

  const rows = team.data ?? [];
  const chosen = rows.filter((r) => selected.has(r.id));
  const totalChosen = chosen.reduce((s, r) => s + r.minutes, 0);

  const decide = async (decision: 'approve' | 'reject' | 'reopen') => {
    try {
      await review.mutateAsync({
        ids: [...selected],
        decision,
        ...(decision === 'reject' ? { note: note.trim() } : {}),
      });
      toast.success(`gov.ops.time.reviewed.${decision}`, { count: selected.size });
      setSelected(new Set());
      setRejecting(false);
      setNote('');
    } catch (err) {
      toast.error(err, 'gov.ops.time.reviewFailed');
    }
  };

  if (team.isError) {
    return <LoadError onRetry={() => void team.refetch()}>{t('gov.ops.time.loadError')}</LoadError>;
  }

  return (
    <>
      <DataTable
        label={t('gov.ops.time.approvalsTitle')}
        rows={rows}
        getRowKey={(r) => r.id}
        isLoading={team.isLoading}
        emptyLabel={t('gov.ops.time.noApprovals')}
        pageSize={50}
        search={{
          placeholder: t('gov.ops.time.searchTeam'),
          of: (r) => `${r.officerName} ${r.fileReference ?? ''} ${r.description}`,
        }}
        filters={
          <FilterSelect
            label={t('gov.ops.time.status')}
            value={status}
            onChange={(v) => {
              setStatus(v as TimeEntryStatus | 'all');
              setSelected(new Set());
            }}
            allValue={null}
            options={(['submitted', 'approved', 'rejected', 'all'] as const).map((s) => ({
              value: s,
              label: t(s === 'all' ? 'gov.ops.time.allStatuses' : `gov.ops.time.statuses.${s}`),
            }))}
          />
        }
        selection={{
          selected,
          onChange: setSelected,
          disabled: review.isPending,
          actions: (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2">
                {t('gov.ops.time.selected', {
                  count: selected.size,
                  duration: formatMinutes(totalChosen),
                })}
              </Typography>
              {chosen.some((r) => r.status !== 'approved') && (
                <Button size="small" variant="contained" onClick={() => void decide('approve')}>
                  {t('gov.ops.time.approve')}
                </Button>
              )}
              {chosen.some((r) => r.status !== 'rejected') && (
                <Button size="small" variant="outlined" onClick={() => setRejecting(true)}>
                  {t('gov.ops.time.reject')}
                </Button>
              )}
              {chosen.length > 0 && chosen.every((r) => r.status !== 'submitted') && (
                <Button size="small" onClick={() => void decide('reopen')}>
                  {t('gov.ops.time.reopen')}
                </Button>
              )}
            </Stack>
          ),
        }}
        columns={[
          {
            key: 'officer',
            label: t('gov.ops.time.officer'),
            primary: true,
            sortValue: (r) => r.officerName,
            cell: (r) => r.officerName,
          },
          {
            key: 'date',
            label: t('gov.ops.time.date'),
            sortValue: (r) => r.workDate,
            cell: (r) => formatDate(r.workDate, i18n.language),
          },
          {
            key: 'what',
            label: t('gov.ops.time.what'),
            cell: (r) => (
              <Stack>
                <Typography variant="body2">
                  {r.fileReference ?? t('gov.ops.time.general')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[t(`gov.ops.time.sources.${r.source}`), r.description]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'minutes',
            label: t('gov.ops.time.duration'),
            align: 'right',
            sortValue: (r) => r.minutes,
            cell: (r) => (
              <Stack sx={{ alignItems: 'flex-end' }}>
                <span>{formatMinutes(r.minutes)}</span>
                {!r.billable && (
                  <Typography variant="caption" color="text.secondary">
                    {t('gov.ops.time.notBillable')}
                  </Typography>
                )}
              </Stack>
            ),
          },
          {
            key: 'status',
            label: t('gov.ops.time.status'),
            sortValue: (r) => r.status,
            cell: (r) => <EntryStatus entry={r} />,
          },
        ]}
      />
      <Dialog open={rejecting} onClose={() => setRejecting(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.ops.time.rejectTitle', { count: selected.size })}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('gov.ops.time.rejectHint')}
          </Alert>
          <TextField
            label={t('gov.ops.time.rejectReason')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            autoFocus
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(false)}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!note.trim() || review.isPending}
            onClick={() => void decide('reject')}
          >
            {t('gov.ops.time.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
